import {
	CORE_PROJECT,
	SOFTWARE_PROJECT,
	type ProjectType,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import type {
	AnalyticsResult,
	BaseFieldConfiguration,
	FieldType,
	ViewConfiguration,
} from '@atlassian/ui-modifications-core/types';
import {
	LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
	LOOKUP_SOURCE_USERS_ASYNC_DATA,
} from '@atlassian/ui-modifications-core/constants';
import {
	GENERIC_FORGE_USE_PRODUCT_MODALS,
	GENERIC_GLOBAL_CREATE_BUTTON,
	GENERIC_KEYBOARD_SHORTCUT,
	JSW_CREATE_EPIC_ISSUE,
	JSW_ISSUE_VIEW_CREATE_ISSUE_IN_EPIC,
	JSW_ISSUE_VIEW_CREATE_SUBTASK,
} from '@atlassian/jira-packages-controllers-use-trigger-issue-create-modal/src/constants.tsx';
import {
	AFFECTS_VERSIONS_TYPE,
	ASSIGNEE_TYPE,
	BASELINE_END_CF_TYPE,
	BASELINE_START_CF_TYPE,
	CASCADING_SELECT_CF_TYPE,
	COMPONENTS_TYPE,
	DATETIME_CF_TYPE,
	DATE_CF_TYPE,
	DESCRIPTION_TYPE,
	DUE_DATE_TYPE,
	FIX_VERSIONS_TYPE,
	ISSUE_TYPE,
	LABELS_TYPE,
	MULTI_CHECKBOXES_CF_TYPE,
	MULTI_SELECT_CF_TYPE,
	MULTI_USER_CF_TYPE,
	NUMBER_CF_TYPE,
	PARENT_TYPE,
	PEOPLE_CF_TYPE,
	PRIORITY_TYPE,
	RADIO_BUTTONS_CF_TYPE,
	REPORTER_TYPE,
	SELECT_CF_TYPE,
	SUMMARY_TYPE,
	TEXT_AREA_CF_TYPE,
	TEXT_CF_TYPE,
	URL_CF_TYPE,
	USER_CF_TYPE,
} from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	cancelTTI,
	markFieldsRegistered,
	markIframeLoad,
	markOnInitResolved,
	stopTTI,
} from '@atlassian/jira-ui-modifications-analytics/src/common/utils/performance-analytics/gic.tsx';
import {
	assigneeGetValueTransformer,
	assigneeSetValueTransformer,
	assigneeSetValueValidator,
} from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/assignee.tsx';
import { validateCascadingSelectSetValue } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/cascading-select.tsx';
import { componentsRequiredScopes } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/components.tsx';
import {
	datePickerGetValueTransformer,
	datePickerSetValueTransformer,
	datePickerSetValueValidator,
} from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/date-picker.tsx';
import {
	transformDateTimePickerGetValue,
	transformDateTimePickerSetValue,
	validateDateTimePickerSetValue,
} from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/date-time-picker.tsx';
import { issueTypeSetValueValidator } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/issue-type.tsx';
import {
	labelsGetValueTransformer,
	labelsSetValueTransformer,
	labelsSetValueValidator,
} from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/labels.tsx';
import { validateNumberValue } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/number.tsx';
import { validateParentWithAriSetValue } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/parent.tsx';
import { validatePrioritySetValue } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/priority.tsx';
import { radioButtonsSetValueValidator } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/radio-buttons.tsx';
import { reporterSetValueValidator } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/reporter.tsx';
import { userPickerSetValueValidator } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/user-picker.tsx';
import { validateVersionValue } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/fields/versions.tsx';
import { getCommonFieldValidators } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/get-common-field-validators/index.tsx';
import { validateSetOptionsVisibilityRule } from '@atlassian/jira-ui-modifications-common/src/common/utils/common/utils/options-visibility/index.tsx';
import { validateSingleLineTextValue } from '@atlassian/ui-modifications-public-api-utils/field/single-line-text';
import { selectSetValueValidator } from '@atlassian/ui-modifications-public-api-utils/field/select';
import {
	FORGE_CUSTOM_FIELD_TYPE_STRING,
	FORGE_CUSTOM_FIELD_TYPE_NUMBER,
} from '@atlassian/jira-ui-modifications-common/src/common/utils/common/constants.tsx';
import type { IssueCreateExecutionContext } from '../../types/context.tsx';
import {
	transformGetValueCascadingSelect,
	transformSetValueCascadingSelect,
} from './fields/cascading-select.tsx';
import {
	componentsGetValueTransformer,
	componentsSetValueTransformer,
	validateComponentsSetValue,
} from './fields/components.tsx';
import {
	descriptionAndParagraphGetValueTransformer,
	descriptionAndParagraphSetValueTransformer,
	validateDescriptionAndParagraphSetValue,
} from './fields/description.tsx';
import {
	issueTypeGetValueTransformer,
	issueTypeSetValueTransformer,
	transformIssueTypeGetOptionsVisibility,
	transformIssueTypeSetOptionsVisibility,
} from './fields/issue-type.tsx';
import {
	multiCheckboxGetValueTransformer,
	multiCheckboxSetValueTransformer,
	transformMultiCheckboxGetOptionsVisibility,
	transformMultiCheckboxSetOptionsVisibility,
	validateMultiCheckboxSetValue,
} from './fields/multi-checkbox.tsx';
import {
	multiSelectGetValueTransformer,
	multiSelectSetValueTransformer,
	transformMultiSelectGetOptionsVisibility,
	transformMultiSelectSetOptionsVisibility,
	validateMultiSelectSetValue,
} from './fields/multi-select.tsx';
import {
	multiUserPickerGetValueTransformer,
	multiUserPickerSetValueTransformer,
	multiUserPickerSetValueValidator,
} from './fields/multi-user-picker.tsx';
import {
	numberIssueGicScopes,
	transformNumberToInternalValue,
	transformNumberToPublicValue,
} from './fields/number.tsx';
import {
	parentGetValueTransformer,
	parentSetValueTransformer,
} from './fields/parent-field/index.tsx';
import {
	peopleGetValueTransformer,
	peopleSetValueTransformer,
	peopleSetValueValidator,
} from './fields/people.tsx';
import {
	prioritySetValueTransformer,
	transformPriorityGetOptionsVisibility,
	transformPriorityGetValue,
	transformPrioritySetOptionsVisibility,
} from './fields/priority.tsx';
import {
	radioButtonsGetValueTransformer,
	radioButtonsSetValueTransformer,
	transformRadioButtonsGetOptionsVisibility,
	transformRadioButtonsSetOptionsVisibility,
} from './fields/radio-buttons.tsx';
import { reporterGetValueTransformer, reporterSetValueTransformer } from './fields/reporter.tsx';
import {
	selectGetValueTransformer,
	selectSetValueTransformer,
	transformSelectGetOptionsVisibility,
	transformSelectSetOptionsVisibility,
} from './fields/select.tsx';
import { singleLineTextFieldGetValueTransformer } from './fields/single-line-text.tsx';
import {
	userPickerGetValueTransformer,
	userPickerSetValueTransformer,
} from './fields/user-picker.tsx';
import {
	fixVersionsSetValueValidator,
	transformVersionsGetValue,
	transformVersionsSetValue,
	versionsRequiredScopes,
} from './fields/versions.tsx';
import messages from './messages.tsx';

export class GICViewConfiguration
	implements ViewConfiguration<BaseFieldConfiguration, IssueCreateExecutionContext>
{
	private analytics: AnalyticsResult = {
		stopTTI,
		cancelTTI,
		markIframeLoad,
		markFieldsRegistered,
		markOnInitResolved,
	};

	private supportedProjectTypes: ProjectType[] = [SOFTWARE_PROJECT, CORE_PROJECT];

	getSupportedFieldsConfiguration(): Record<FieldType, BaseFieldConfiguration> {
		const commonFieldValidators = getCommonFieldValidators();

		return {
			[SUMMARY_TYPE]: {
				// It contains also supported field properties.
				// Validator MUST be added here to make given property supported.
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateSingleLineTextValue,
				},
				// Transformers are optional per property, if missing the value will be used as is
				publicToInternalTransformers: {},
				internalToPublicTransformers: {
					value: singleLineTextFieldGetValueTransformer,
				},
				emitChangeOn: 'blur',
			},
			[PRIORITY_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validatePrioritySetValue,
					optionsVisibility: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: prioritySetValueTransformer,
					optionsVisibility: transformPrioritySetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: transformPriorityGetValue,
					optionsVisibility: transformPriorityGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				propertyMapping: {
					optionsVisibility: 'allowedValues',
				},
			},
			[ASSIGNEE_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: assigneeSetValueValidator,
				},
				publicToInternalTransformers: {
					value: assigneeSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: assigneeGetValueTransformer,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
			[USER_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: userPickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: userPickerSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: userPickerGetValueTransformer,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
			[LABELS_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: labelsSetValueValidator,
				},
				publicToInternalTransformers: {
					value: labelsSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: labelsGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[DESCRIPTION_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateDescriptionAndParagraphSetValue,
				},
				publicToInternalTransformers: {
					value: descriptionAndParagraphSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: descriptionAndParagraphGetValueTransformer,
				},
				emitChangeOn: 'blur',
			},
			[SELECT_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: selectSetValueValidator,
					optionsVisibility: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: selectSetValueTransformer,
					optionsVisibility: transformSelectSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: selectGetValueTransformer,
					optionsVisibility: transformSelectGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				propertyMapping: {
					optionsVisibility: 'allowedValues',
				},
			},
			[TEXT_AREA_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateDescriptionAndParagraphSetValue,
				},
				publicToInternalTransformers: {
					value: descriptionAndParagraphSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: descriptionAndParagraphGetValueTransformer,
				},
				emitChangeOn: 'blur',
			},
			[MULTI_SELECT_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateMultiSelectSetValue,
					optionsVisibility: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: multiSelectSetValueTransformer,
					optionsVisibility: transformMultiSelectSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: multiSelectGetValueTransformer,
					optionsVisibility: transformMultiSelectGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				propertyMapping: {
					optionsVisibility: 'allowedValues',
				},
			},
			[PEOPLE_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: peopleSetValueValidator,
				},
				publicToInternalTransformers: {
					value: peopleSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: peopleGetValueTransformer,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
			[MULTI_USER_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: multiUserPickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: multiUserPickerSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: multiUserPickerGetValueTransformer,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
			[TEXT_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateSingleLineTextValue,
				},
				publicToInternalTransformers: {},
				internalToPublicTransformers: {
					value: singleLineTextFieldGetValueTransformer,
				},
				emitChangeOn: 'blur',
			},
			[MULTI_CHECKBOXES_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateMultiCheckboxSetValue,
					optionsVisibility: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: multiCheckboxSetValueTransformer,
					optionsVisibility: transformMultiCheckboxSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: multiCheckboxGetValueTransformer,
					optionsVisibility: transformMultiCheckboxGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				propertyMapping: {
					optionsVisibility: 'allowedValues',
				},
			},
			[COMPONENTS_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateComponentsSetValue,
				},
				publicToInternalTransformers: {
					value: componentsSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: componentsGetValueTransformer,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				requiredScopes: componentsRequiredScopes,
			},
			[REPORTER_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: reporterSetValueValidator,
				},
				publicToInternalTransformers: {
					value: reporterSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: reporterGetValueTransformer,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_USERS_ASYNC_DATA,
			},
			[FIX_VERSIONS_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: fixVersionsSetValueValidator,
				},
				publicToInternalTransformers: {
					value: transformVersionsSetValue,
				},
				internalToPublicTransformers: {
					value: transformVersionsGetValue,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				requiredScopes: versionsRequiredScopes,
			},
			[ISSUE_TYPE]: {
				publicShapeValidators: {
					value: issueTypeSetValueValidator,
					optionsVisibility: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: issueTypeSetValueTransformer,
					optionsVisibility: transformIssueTypeSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: issueTypeGetValueTransformer,
					optionsVisibility: transformIssueTypeGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				propertyMapping: {
					optionsVisibility: 'allowedValues',
				},
			},
			[URL_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateSingleLineTextValue,
				},
				publicToInternalTransformers: {},
				internalToPublicTransformers: {
					value: singleLineTextFieldGetValueTransformer,
				},
				emitChangeOn: 'blur',
			},
			[DATE_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: datePickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: datePickerSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: datePickerGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[DATETIME_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateDateTimePickerSetValue,
				},
				publicToInternalTransformers: {
					value: transformDateTimePickerSetValue,
				},
				internalToPublicTransformers: {
					value: transformDateTimePickerGetValue,
				},
				emitChangeOn: 'change',
			},
			[RADIO_BUTTONS_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: radioButtonsSetValueValidator,
					optionsVisibility: validateSetOptionsVisibilityRule,
				},
				publicToInternalTransformers: {
					value: radioButtonsSetValueTransformer,
					optionsVisibility: transformRadioButtonsSetOptionsVisibility,
				},
				internalToPublicTransformers: {
					value: radioButtonsGetValueTransformer,
					optionsVisibility: transformRadioButtonsGetOptionsVisibility,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				propertyMapping: {
					optionsVisibility: 'allowedValues',
				},
			},
			[NUMBER_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateNumberValue,
				},
				publicToInternalTransformers: {
					value: transformNumberToInternalValue,
				},
				internalToPublicTransformers: {
					value: transformNumberToPublicValue,
				},
				emitChangeOn: 'blur',
				requiredScopes: numberIssueGicScopes,
			},
			[PARENT_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateParentWithAriSetValue,
				},
				publicToInternalTransformers: { value: parentSetValueTransformer },
				internalToPublicTransformers: {
					value: parentGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[CASCADING_SELECT_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateCascadingSelectSetValue,
				},
				publicToInternalTransformers: {
					value: transformSetValueCascadingSelect,
				},
				internalToPublicTransformers: {
					value: transformGetValueCascadingSelect,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
			},
			[AFFECTS_VERSIONS_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateVersionValue,
				},
				publicToInternalTransformers: {
					value: transformVersionsSetValue,
				},
				internalToPublicTransformers: {
					value: transformVersionsGetValue,
				},
				emitChangeOn: 'change',
				lookupSource: LOOKUP_SOURCE_ALLOWED_VALUES_PROPERTY,
				requiredScopes: versionsRequiredScopes,
			},
			[DUE_DATE_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: datePickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: datePickerSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: datePickerGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[BASELINE_START_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: datePickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: datePickerSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: datePickerGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[BASELINE_END_CF_TYPE]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: datePickerSetValueValidator,
				},
				publicToInternalTransformers: {
					value: datePickerSetValueTransformer,
				},
				internalToPublicTransformers: {
					value: datePickerGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[FORGE_CUSTOM_FIELD_TYPE_STRING]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateSingleLineTextValue,
				},
				publicToInternalTransformers: {},
				internalToPublicTransformers: {
					value: singleLineTextFieldGetValueTransformer,
				},
				emitChangeOn: 'change',
			},
			[FORGE_CUSTOM_FIELD_TYPE_NUMBER]: {
				publicShapeValidators: {
					...commonFieldValidators,
					value: validateNumberValue,
				},
				publicToInternalTransformers: {
					value: transformNumberToInternalValue,
				},
				internalToPublicTransformers: {
					value: transformNumberToPublicValue,
				},
				emitChangeOn: 'change',
			},
		};
	}

	getSupportedFieldTypes() {
		const supportedFieldTypes: FieldType[] = [
			SUMMARY_TYPE,
			PRIORITY_TYPE,
			ASSIGNEE_TYPE,
			LABELS_TYPE,
			DESCRIPTION_TYPE,
			SELECT_CF_TYPE,
			TEXT_AREA_CF_TYPE,
			MULTI_SELECT_CF_TYPE,
			USER_CF_TYPE,
			TEXT_CF_TYPE,
			MULTI_USER_CF_TYPE,
			PEOPLE_CF_TYPE,
			MULTI_CHECKBOXES_CF_TYPE,
			REPORTER_TYPE,
			COMPONENTS_TYPE,
			FIX_VERSIONS_TYPE,
			ISSUE_TYPE,
			DATE_CF_TYPE,
			DATETIME_CF_TYPE,
			URL_CF_TYPE,
			RADIO_BUTTONS_CF_TYPE,
			NUMBER_CF_TYPE,
			PARENT_TYPE,
			AFFECTS_VERSIONS_TYPE,
			DUE_DATE_TYPE,
			BASELINE_START_CF_TYPE,
			BASELINE_END_CF_TYPE,
			CASCADING_SELECT_CF_TYPE,
		];

		if (fg('ditto_forge_uim_support_for_fcf_string')) {
			supportedFieldTypes.push(FORGE_CUSTOM_FIELD_TYPE_STRING);
		}
		if (fg('ditto_forge_uim_support_for_fcf_number')) {
			supportedFieldTypes.push(FORGE_CUSTOM_FIELD_TYPE_NUMBER);
		}

		return supportedFieldTypes;
	}

	isExecutionContextSupported({ context }: { context: IssueCreateExecutionContext }): boolean {
		if (!context.project) {
			return false;
		}

		return this.supportedProjectTypes.includes(context.project.type);
	}

	getConsentMessage() {
		if (fg('jira-issue-terminology-refresh-m3')) {
			if (fg('product-terminology-refresh')) {
				return messages.obligatoryUimGicConsentMessageIssueTermRefreshAppify;
			}
			return messages.obligatoryUimGicConsentMessageIssueTermRefresh;
		}
		return messages.obligatoryUimGicConsentMessage;
	}

	getAnalytics() {
		return this.analytics;
	}

	getSupportedTriggerPointsConfiguration() {
		return new Set([
			GENERIC_FORGE_USE_PRODUCT_MODALS,
			GENERIC_GLOBAL_CREATE_BUTTON,
			GENERIC_KEYBOARD_SHORTCUT,
			JSW_ISSUE_VIEW_CREATE_ISSUE_IN_EPIC,
			JSW_CREATE_EPIC_ISSUE,
			JSW_ISSUE_VIEW_CREATE_SUBTASK,
		] as const);
	}
}
